import { NotificationType, useAvatarImage } from "@next-platform/client-common-core";
import { type RefObject, useState } from "react";

import useToast from "@/hooks/useToast";
import characterFormLogger from "@/infra/characterFormLogger";
import { pngCharacterCard } from "@/utils";

const IMAGE_PICKER_INPUT_ID = "image-picker";

type UseAvatarUploadProps = {
  setCharacterAvatar?: (avatar: string) => void;
  imagePickerRef?: RefObject<HTMLInputElement | null>;
};

const getImagePickerInput = (imagePickerRef?: RefObject<HTMLInputElement | null>): HTMLInputElement | null => {
  return imagePickerRef?.current ?? (document.getElementById(IMAGE_PICKER_INPUT_ID) as HTMLInputElement | null);
};

const resetImagePickerInput = (imagePickerInput: HTMLInputElement) => {
  imagePickerInput.value = "";
};

type ImageFileReadSuccess = {
  base64Data: string;
  contentType: string;
  fileName: string;
};

const readImageFileAsBase64 = ({
  selectedFile,
  imagePickerInput,
  onSuccess,
  onError,
}: {
  selectedFile: File;
  imagePickerInput: HTMLInputElement;
  onSuccess: (result: ImageFileReadSuccess) => void;
  onError: (readError: ProgressEvent<FileReader>) => void;
}) => {
  const reader = new FileReader();
  reader.onload = () => {
    const dataURL = reader.result as string;
    resetImagePickerInput(imagePickerInput);
    onSuccess({
      contentType: dataURL.split(";")[0].split(":")[1],
      base64Data: dataURL.split(",")[1],
      fileName: selectedFile.name,
    });
  };
  reader.onerror = (readError) => {
    resetImagePickerInput(imagePickerInput);
    onError(readError);
  };
  reader.readAsDataURL(selectedFile);
};

type ImportedJsonDataType = null | Record<string, unknown>;

// Function to strip URLs from a string
function stripUrls(string: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return string.replace(urlRegex, "");
}

const parseImportedCharacterJson = (characterData: ImportedJsonDataType): ImportedJsonDataType => {
  if (characterData?.spec === "chara_card_v2" && characterData?.data) {
    return characterData.data as Record<string, unknown>;
  }
  return characterData;
};

const stripUrlsFromImportedCharacter = (characterData: Record<string, unknown>): Record<string, unknown> => {
  const strippedCharacterData: Record<string, unknown> = { ...characterData };
  Object.keys(strippedCharacterData).forEach((fieldName) => {
    const fieldValue = strippedCharacterData[fieldName];
    if (typeof fieldValue === "string") {
      strippedCharacterData[fieldName] = stripUrls(fieldValue);
    } else if (typeof fieldValue === "object") {
      strippedCharacterData[fieldName] = stripUrls(JSON.stringify(fieldValue));
    }
  });
  return strippedCharacterData;
};

const readCharacterDataFromPngFile = async (file: File): Promise<{ characterData: ImportedJsonDataType; image: ArrayBuffer }> => {
  const image = await file.arrayBuffer();
  const parsedCharacterData = parseImportedCharacterJson(JSON.parse(pngCharacterCard.Parse(image)));
  if (parsedCharacterData === null) {
    return { characterData: parsedCharacterData, image };
  }
  return { characterData: stripUrlsFromImportedCharacter(parsedCharacterData), image };
};

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const useAvatarUpload = ({ setCharacterAvatar, imagePickerRef }: UseAvatarUploadProps = {}) => {
  const [stateLocalFilename, setStateLocalFilename] = useState("No File Chosen");
  const [stateIsImageUploading, setStateIsImageUploading] = useState(false);
  const [stateImportedJsonData, setStateImportedJsonData] = useState<ImportedJsonDataType>(null);
  const [stateAvatarFileKey, setStateAvatarFileKey] = useState<string | null>(null);
  const { onUserSaveAvatarImage } = useAvatarImage();
  const { displayToast } = useToast();

  const saveUploadedFile = async (base64Data: string, contentType: string) => {
    const { data: result, error } = await onUserSaveAvatarImage({
      image_type: contentType,
      imageData: base64Data,
    });
    if (error) {
      characterFormLogger.error("Error while saving avatar image:", error);
      setStateIsImageUploading(false);
      displayToast({ message: error, type: NotificationType.error });
      return;
    }
    setStateIsImageUploading(false);
    setStateAvatarFileKey(result?.key ?? null);
    setCharacterAvatar?.(result?.key ?? "");
  };

  const onCharacterCardUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json, image/png";
    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files![0];
      let characterData: ImportedJsonDataType = null;
      if (file.type === "application/json") {
        characterData = JSON.parse(await file.text());
      } else if (file.type === "image/png") {
        try {
          const pngImportResult = await readCharacterDataFromPngFile(file);
          characterData = pngImportResult.characterData;
          saveUploadedFile(arrayBufferToBase64(pngImportResult.image), "image/png").catch((uploadError: unknown) => {
            characterFormLogger.error("Error while saving avatar image:", uploadError);
          });
        } catch (importError) {
          throw new Error("Error with import", {
            cause: importError,
          });
        }
      }

      setStateImportedJsonData(characterData);
    };
    input.click();
  };

  const clearImportedJsonData = () => {
    setStateImportedJsonData(null);
  };

  const onImageUpload = () => {
    const imagePickerInput = getImagePickerInput(imagePickerRef);
    if (!imagePickerInput) {
      return;
    }

    resetImagePickerInput(imagePickerInput);
    imagePickerInput.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const selectedFile = target.files?.[0];
      if (!selectedFile) {
        return;
      }
      setStateIsImageUploading(true);
      readImageFileAsBase64({
        selectedFile,
        imagePickerInput: target,
        onSuccess: ({ base64Data, contentType, fileName }) => {
          saveUploadedFile(base64Data, contentType).catch((uploadError: unknown) => {
            setStateIsImageUploading(false);
            characterFormLogger.error("Error while saving avatar image:", uploadError);
          });
          setStateLocalFilename(fileName);
        },
        onError: (readError) => {
          setStateIsImageUploading(false);
          displayToast({ message: "Error uploading image", type: NotificationType.error });
          characterFormLogger.error(readError);
        },
      });
    };
    imagePickerInput.click();
  };

  return {
    onImageUpload,
    localFilename: stateLocalFilename,
    avatarFileKey: stateAvatarFileKey,
    onCharacterCardUpload,
    importedJsonData: stateImportedJsonData,
    isImageUploading: stateIsImageUploading,
    clearImportedJsonData,
  };
};

export default useAvatarUpload;
